/**
 * What the trigger form offers, and what it can be handed.
 *
 * The shared catalog in `shared/domain/triggers` mirrors the backend and is
 * parity-tested against it, so it keeps every entity and every change. Which
 * of them this form puts in front of a person is a policy of the form, and it
 * lives here. The UI descriptions answer with a tag rather than with words, so
 * the component that renders one owns the copy.
 */

import { type InvocationInput } from "@/shared/domain/actions";
import {
  actionTakesTheFile,
  entityOfSubscription,
  isTriggeringSession,
  TriggerSourceType,
  TriggerTargetType,
  variableRoots,
  type PlatformEntity,
  type TriggerV2Record,
} from "@/shared/domain/triggers";

import { type TriggerFormValues } from "./triggerFormSchema";

/** The entities the form watches, a subset of the catalog's. */
export const TRIGGER_FORM_ENTITIES = [
  "file",
  "dataset",
  "session",
  "invocation",
] as const satisfies readonly PlatformEntity[];

export type TriggerFormEntity = (typeof TRIGGER_FORM_ENTITIES)[number];

/** The kinds of target the picker offers, in the order it shows them. */
export const TARGET_KINDS = [
  TriggerTargetType.InvokeAction,
  TriggerTargetType.StartAgent,
  TriggerTargetType.SendSlackMessage,
] as const satisfies readonly TriggerTargetType[];

/** The kind a new target starts on: the first the picker shows. */
export const DEFAULT_TARGET_KIND = TARGET_KINDS[0];

/**
 * Whether the input selects topics, or sessions other than the triggering one
 * a session trigger always passes (see `TRIGGERING_SESSION_SELECTOR`).
 */
function selectsAnythingButFiles(
  input: InvocationInput | null | undefined,
  passesTheSession: boolean,
): boolean {
  if (input === null || input === undefined) {
    return false;
  }
  const sessions =
    passesTheSession && isTriggeringSession(input.sessions)
      ? null
      : input.sessions;
  return chosen(input.topics) || chosen(sessions);
}

function chosen(selector: unknown): boolean {
  return Array.isArray(selector)
    ? selector.length > 0
    : selector !== null && selector !== undefined;
}

/**
 * Whether the edit route can open a stored trigger without losing anything it
 * holds. A trigger it turns away is still viewed, enabled, disabled, and
 * deleted from its details page; filling the form halfway and saving would
 * silently rewrite what the form has no controls for.
 */
export function isEditableInForm(record: TriggerV2Record): boolean {
  // The form's query rows select files only; a stored topics or sessions
  // selector has nowhere to go, except the triggering session.
  const passesTheSession =
    record.fires_on.type === TriggerSourceType.Events &&
    entityOfSubscription(record.fires_on.events) === "session";
  const selectsOnlyFiles = record.targets.every(
    (target) =>
      target.type !== TriggerTargetType.InvokeAction ||
      !selectsAnythingButFiles(target.invocation_input, passesTheSession),
  );
  if (!selectsOnlyFiles) {
    return false;
  }
  if (record.fires_on.type === TriggerSourceType.Schedule) {
    return true;
  }
  // Null for a subscription that mixes entities, which has no single one for
  // the entity Select; "event" for a subscription to annotation events, which
  // the form never shows.
  const entity = entityOfSubscription(record.fires_on.events);
  return TRIGGER_FORM_ENTITIES.some((offered) => offered === entity);
}

/**
 * What the change itself supplies to an action, shown at the top of "Input
 * data". Null on a schedule, which fires with nothing, and on a change that
 * carries no files or session, such as an invocation's.
 */
export function suppliedInputFor(
  firesOn: TriggerFormValues["firesOn"],
): "changed-file" | "changed-dataset" | "changed-session" | null {
  if (firesOn.type === TriggerSourceType.Schedule) {
    return null;
  }
  const entity = entityOfSubscription(firesOn.events);
  if (entity === "session") {
    return "changed-session";
  }
  if (entity !== "file" && entity !== "dataset") {
    return null;
  }
  return actionTakesTheFile(firesOn.events, firesOn.oncePer)
    ? "changed-file"
    : "changed-dataset";
}

/**
 * Whether every change the trigger watches puts a dataset in the namespace.
 * Only then do `{{dataset.*}}` placeholders resolve, and only then does the
 * server accept file patterns on an action target.
 */
export function carriesADataset(
  firesOn: TriggerFormValues["firesOn"],
): boolean {
  return (
    firesOn.type === TriggerSourceType.Events &&
    variableRoots(firesOn.events).includes("dataset")
  );
}

/**
 * Whether the source carries the fields of the kind its `type` names.
 *
 * The run-mode control writes only that tag, and the cascade replaces the rest
 * of the source in answer, so between the two writes a reader can see a
 * schedule with a subscription's fields under it. Nothing should be read off
 * one in that state.
 */
export function isSourceComplete(
  firesOn: TriggerFormValues["firesOn"],
): boolean {
  return firesOn.type === TriggerSourceType.Schedule
    ? typeof firesOn.time === "string" && typeof firesOn.cron === "string"
    : Array.isArray(firesOn.events);
}

/** Which submit the bar shows. */
export function submitKindFor(
  mode: "create" | "edit",
  enabled: boolean,
): "create" | "create-and-enable" | "save" {
  if (mode === "edit") {
    return "save";
  }
  return enabled ? "create-and-enable" : "create";
}
